import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../lib/AuthContext';
import { Button } from '../components/ui/button';
import { Menu, X } from 'lucide-react';

const DirectMessages = () => {
  const { user, accessToken } = useAuth();
  const [users, setUsers] = useState([]);
  const [receiverId, setReceiverId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unreadCounts, setUnreadCounts] = useState({});
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const fetchUnreadCounts = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch("https://quirkleai.com/api/dms/unread_counts/", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) setUnreadCounts(await res.json());
    } catch (err) {
      console.error("Error fetching unread counts:", err);
    }
  }, [accessToken]);

  // Fetch users
  useEffect(() => {
    const url =
      user?.role === "client"
        ? "https://quirkleai.com/api/therapists/"
        : "https://quirkleai.com/api/myclients/";

    fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => res.json())
      .then((data) => setUsers(data.filter((u) => u.id !== user.id)));

    fetchUnreadCounts();
  }, [user, accessToken, fetchUnreadCounts]);

  // Fetch messages
  useEffect(() => {
    if (!receiverId) return;

    fetch(`https://quirkleai.com/api/dms/?receiver=${receiverId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => res.json())
      .then(async (data) => {
        const filtered = data.filter(
          (msg) =>
            (msg.sender === user.id && msg.receiver === receiverId) ||
            (msg.receiver === user.id && msg.sender === receiverId)
        );

        setMessages(filtered);

        // Mark unread
        const promises = filtered.map(async (msg) => {
          if (!msg.is_read && msg.receiver === user.id) {
            await fetch(`https://quirkleai.com/api/dms/${msg.id}/`, {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ is_read: true }),
            });
          }
        });

        await Promise.all(promises);
        fetchUnreadCounts();
      });
  }, [receiverId, accessToken, user, fetchUnreadCounts]);

  const sendMessage = async () => {
    if (!receiverId || !newMessage.trim()) return;

    const res = await fetch("https://quirkleai.com/api/dms/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ receiver: receiverId, message: newMessage }),
    });

    if (res.ok) {
      const msg = await res.json();
      setMessages((prev) => [...prev, msg]);
      setNewMessage("");
      fetchUnreadCounts();
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-[82vh] bg-white border rounded shadow relative">

      {/* Mobile top bar */}
      <div className="md:hidden flex items-center p-3 border-b">
        <button onClick={() => setMobileSidebarOpen(true)}>
          <Menu className="w-6 h-6 text-gray-700" />
        </button>
        <h2 className="text-lg ml-3 font-semibold text-gray-800">Messages</h2>
      </div>

      {/* Sidebar */}
      <div
        className={`
          fixed md:static top-0 left-0 h-full w-72 bg-white border-r shadow-md z-50 
          transform transition-transform duration-300 
          ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
      >
        {/* Mobile close button */}
        <div className="flex items-center justify-between p-4 md:hidden border-b">
          <h2 className="text-lg font-semibold">Users</h2>
          <button onClick={() => setMobileSidebarOpen(false)}>
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto h-full">
          {users.map((u) => (
            <div
              key={u.id}
              onClick={() => {
                setReceiverId(u.id);
                setMobileSidebarOpen(false);
              }}
              className={`p-3 rounded cursor-pointer mb-2 flex justify-between items-center ${
                receiverId === u.id ? "bg-indigo-100 font-semibold" : "hover:bg-gray-100"
              }`}
            >
              <span>{u.username}</span>
              {unreadCounts[u.id] > 0 && (
                <span className="px-2 py-1 bg-red-500 text-white rounded-full text-xs">
                  {unreadCounts[u.id]}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col p-3 md:p-4">
        {/* Chat header */}
        {receiverId ? (
          <div className="border-b pb-3 mb-3 text-lg font-semibold text-gray-700">
            Chat with {users.find((u) => u.id === receiverId)?.username}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            Select a user to start chatting
          </div>
        )}

        {/* Messages */}
        {receiverId && (
          <>
            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`max-w-[75%] px-4 py-2 rounded-md text-sm break-words ${
                    msg.sender === user.id
                      ? "bg-indigo-200 ml-auto text-right"
                      : "bg-gray-100 mr-auto"
                  }`}
                >
                  <p>{msg.message}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {msg.sender === user.id ? "You" : msg.sender_username} •{" "}
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              ))}
            </div>

            {/* Input */}
            <div className="mt-3 flex gap-2">
              <input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Type a message..."
                className="border p-2 rounded flex-1"
              />
              <Button onClick={sendMessage}>Send</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DirectMessages;
